import React, { useState } from 'react';
import IconButton from 'material-ui/IconButton';
import RaisedButton from 'material-ui/RaisedButton';
import Paper from 'material-ui/Paper';
import ClearIcon from 'material-ui/svg-icons/content/clear';
import ExpandLessIcon from 'material-ui/svg-icons/navigation/expand-less';
import ExpandMoreIcon from 'material-ui/svg-icons/navigation/expand-more';
import SendIcon from 'material-ui/svg-icons/content/send';
import { useMutation } from 'react-query';

import { sendTrainingBatch } from 'services/gazetters';
import { useBatch, BATCH_TYPES } from './BatchContext';
import { MAX_BATCH_SIZE } from './utils';

const styles = {
    container: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000,
        width: '320px',
    },
    paper: {
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: '#1976d2',
        color: 'white',
        cursor: 'pointer',
    },
    headerTitle: {
        fontSize: '14px',
        fontWeight: 500,
    },
    headerIcon: {
        color: 'white',
        width: 20,
        height: 20,
    },
    content: {
        maxHeight: '400px',
        overflowY: 'auto',
    },
    section: {
        borderBottom: '1px solid #e0e0e0',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: '#f5f5f5',
        fontSize: '12px',
        fontWeight: 500,
    },
    sectionCount: {
        fontSize: '11px',
        color: '#666',
    },
    itemList: {
        padding: '0',
        margin: 0,
        listStyle: 'none',
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
        fontSize: '12px',
    },
    itemText: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginRight: '8px',
        fontFamily: 'monospace',
    },
    removeButton: {
        width: 24,
        height: 24,
        padding: 0,
    },
    removeIcon: {
        width: 16,
        height: 16,
        color: '#999',
    },
    sendButton: {
        margin: '8px 12px',
        minWidth: '92%',
    },
    emptyMessage: {
        padding: '12px',
        fontSize: '12px',
        color: '#999',
        fontStyle: 'italic',
        textAlign: 'center',
    },
    successMessage: {
        padding: '8px 12px',
        fontSize: '12px',
        color: '#4CAF50',
        backgroundColor: '#e8f5e9',
    },
    errorMessage: {
        padding: '8px 12px',
        fontSize: '12px',
        color: '#d32f2f',
        backgroundColor: '#ffebee',
    },
    minimizedBadge: {
        display: 'inline-block',
        backgroundColor: '#ff5722',
        color: 'white',
        borderRadius: '10px',
        padding: '2px 8px',
        fontSize: '11px',
        marginLeft: '8px',
    },
};

const BatchSection = ({ 
    title, 
    items, 
    batchType, 
    onRemove,
}) => {
    const count = items.length;
    const isFull = count >= MAX_BATCH_SIZE;

    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader}>
                <span>
                    {title}
                    {isFull && <span style={{ color: '#ff5722', marginLeft: 8 }}>(FULL)</span>}
                </span>
                <span style={styles.sectionCount}>{count}/{MAX_BATCH_SIZE}</span>
            </div>
            
            {items.length === 0 ? (
                <div style={styles.emptyMessage}>No items in batch</div>
            ) : (
                <ul style={styles.itemList}>
                    {items.map(item => (
                        <li key={item.id} style={styles.item}>
                            <span style={styles.itemText} title={item.line}>
                                {item.line}
                            </span>
                            <IconButton 
                                style={styles.removeButton}
                                iconStyle={styles.removeIcon}
                                onClick={() => onRemove(item.id, batchType)}
                            >
                                <ClearIcon />
                            </IconButton>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const BatchManager = ({ mailId }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [successMessages, setSuccessMessages] = useState({});
    const [errorMessages, setErrorMessages] = useState({});
    
    const { 
        removeFromBatch, 
        clearBatch,
        data,
    } = useBatch();

    const sendMutation = useMutation(
        ({ data }) => sendTrainingBatch(mailId, data),
        {
            onSuccess: () => {
                clearBatch();
                setSuccessMessages(prev => ({ 
                    ...prev, 
                    success: true,
                }));
                setErrorMessages(prev => ({ 
                    ...prev, 
                    success: null,
                }));
                setTimeout(() => {
                    setSuccessMessages(prev => ({ 
                        ...prev, 
                        success: false,
                    }));
                }, 3000);
            },
            onError: (error) => {
                setErrorMessages(prev => ({ 
                    ...prev, 
                    error: error.message || 'Failed to send batch',
                }));
            },
        },
    );

    const handleSend = () => {
        if (data.usefulToNotUseful.length === 0 && data.notUsefulToUseful.length === 0) return;

        setErrorMessages(prev => ({ ...prev, error: null }));

        const prepared = {
            usefulToNotUseful: data.usefulToNotUseful.map(i => i.line),
            notUsefulToUseful: data.notUsefulToUseful.map(i => i.line),
        };

        sendMutation.mutate({ data: prepared });
    };

    const totalItems = data.usefulToNotUseful.length + data.notUsefulToUseful.length;

    return (
        <div style={styles.container}>
            <Paper style={styles.paper} zDepth={3}>
                <div 
                    style={styles.header}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <span style={styles.headerTitle}>
                        Training Batch Manager
                        {!isExpanded && totalItems > 0 && (
                            <span style={styles.minimizedBadge}>{totalItems}</span>
                        )}
                    </span>
                    <IconButton 
                        style={{ width: 24, height: 24, padding: 0 }}
                        iconStyle={styles.headerIcon}
                    >
                        {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                </div>

                {isExpanded && (
                    <div style={styles.content}>
                        <BatchSection
                            title="Useful → Not Useful"
                            items={data.usefulToNotUseful}
                            batchType={BATCH_TYPES.USEFUL_TO_NOT_USEFUL}
                            onRemove={removeFromBatch}
                        />
                        <BatchSection
                            title="Not Useful → Useful"
                            items={data.notUsefulToUseful}
                            batchType={BATCH_TYPES.NOT_USEFUL_TO_USEFUL}
                            onRemove={removeFromBatch}
                        />

                        {successMessages.success && (
                            <div style={styles.successMessage}>
                                Batch sent successfully!
                            </div>
                        )}

                        {errorMessages.success && (
                            <div style={styles.errorMessage}>
                                Error: {errorMessages.error}
                            </div>
                        )}

                        {(data.notUsefulToUseful.length > 0 || data.usefulToNotUseful.length > 0) && (
                            <div style={{ display: 'flex', gap: '8px', padding: '8px 12px', alignItems: 'center' }}>
                                <RaisedButton
                                    label="Clear All"
                                    style={{ flex: 1, minWidth: 0 }}
                                    buttonStyle={{ minWidth: 0 }}
                                    labelStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 8px' }}
                                    onClick={() => clearBatch()}
                                    disabled={sendMutation.isLoading}
                                />
                                <RaisedButton
                                    label={sendMutation.isLoading ? 'Sending...' : 'Send Batch'}
                                    primary
                                    icon={<SendIcon />}
                                    style={{ flex: 1, minWidth: 0 }}
                                    buttonStyle={{ minWidth: 0 }}
                                    labelStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 8px' }}
                                    onClick={() => handleSend()}
                                    disabled={sendMutation.isLoading}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Paper>
        </div>
    );
};

export default BatchManager;




// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/Content/BatchManager.jsx